import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutUser } from '../../store/slices/authSlice';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  Receipt,
  CalendarToday,
  ShoppingCart,
  Phone,
  Settings,
  Notifications,
  AccountCircle,
  Category,
  ArrowDropDown,
  Inventory,
} from '@mui/icons-material';

  const navigationItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/' },
    { text: 'Customers', icon: <People />, path: '/customers' },
    { text: 'Suppliers', icon: <Business />, path: '/suppliers' },
    { text: 'Stock', icon: <Inventory />, path: '/stock' },
    { text: 'Invoices', icon: <Receipt />, path: '/invoices' },
    { text: 'Subscriptions', icon: <CalendarToday />, path: '/subscriptions' },
    { text: 'Orders', icon: <ShoppingCart />, path: '/orders' },
    { text: 'Settings', icon: <Settings />, path: '/users' },
    { text: 'Call Center', icon: <Phone />, path: '/call-center' },
  ];

const Layout: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser());
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleProfileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f0f9ff' }}>
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'white',
          color: '#1f2937',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Toolbar sx={{ px: 3, py: 1 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src="/logo.png"
              alt="E7 Logo"
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: '#4A90E2',
                fontWeight: 'normal',
                fontSize: '1.2rem',
                lineHeight: 1,
              }}
            >
              GROUP
            </Typography>
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, justifyContent: 'center' }}>
            {navigationItems.map((item) => (
              <Button
                key={item.text}
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  color: location.pathname === item.path ? '#4A90E2' : '#6b7280',
                  backgroundColor: location.pathname === item.path ? '#f0f9ff' : 'transparent',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? '#f0f9ff' : '#f9fafb',
                  },
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* Right side items */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#6b7280' }}>
              <Settings />
            </IconButton>
            <IconButton sx={{ color: '#6b7280' }}>
              <Badge badgeContent={1} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#4A90E2' }}>
                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500 }}>
                {user?.name || 'User'}
              </Typography>
              <IconButton
                size="small"
                onClick={handleProfileMenuOpen}
                sx={{ color: '#6b7280' }}
              >
                <ArrowDropDown />
              </IconButton>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>Profile</MenuItem>
            <MenuItem onClick={handleProfileMenuClose}>Settings</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
